import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ProductBottle from '../../components/feature/ProductBottle';
import { allProducts } from '../../mocks/products';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function ProductDetailPage() {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState<'description' | 'additional' | 'coa'>('description');
  const [notes, setNotes] = useState('');

  const product = allProducts.find(p => p.slug === productSlug);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-navy-900 mb-4">Product Not Found</h1>
          <Link to="/shop" className="text-primary-600 hover:text-primary-700 cursor-pointer">
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleRequestQuote = () => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/login', { state: { from: `/product/${productSlug}` } });
      return;
    }
    // Open quote form if authenticated
    setShowQuoteForm(true);
  };

  const handleSubmitQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }

    setFormStatus('submitting');

    try {
      const { error } = await supabase
        .from('quote_requests')
        .insert({
          user_id: user.id,
          product_name: product.name,
          dosage: product.specification,
          quantity: quantity,
          notes: notes || null,
          status: 'pending'
        });

      if (error) throw error;

      setFormStatus('success');
      setNotes('');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowQuoteForm(false);
        setFormStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm mb-8">
            <Link to="/shop" className="text-gray-600 hover:text-primary-600 cursor-pointer">
              Shop
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-navy-900 font-medium">{product.name}</span>
          </div>

          {/* Research Use Only Disclaimer Banner */}
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <i className="ri-alert-line text-red-600 text-2xl mr-4 mt-1 flex-shrink-0"></i>
              <div>
                <h3 className="text-lg font-bold text-red-900 mb-2">For Research Use Only</h3>
                <p className="text-sm text-red-800 leading-relaxed">
                  This product is intended solely for laboratory research purposes. Not for human consumption, medical use, veterinary use, or household application. All orders require manual quote approval. Payment is processed only after quote confirmation.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Product Image with DNBio Bottle */}
            <div className="bg-white rounded-2xl p-12 shadow-md">
              <div className="flex items-center justify-center h-80">
                <ProductBottle
                  productName={product.name}
                  specification={product.specification}
                  purity={product.purity}
                  batchNumber={`DB-${product.id.slice(-4).toUpperCase()}`}
                  className="transform scale-150"
                />
              </div>
              {/* DNBio Badge */}
              <div className="mt-8 flex items-center justify-center space-x-2 text-gray-500">
                <img
                  src="https://public.readdy.ai/ai/img_res/41008464-2da7-44e8-a4ad-cffce2d1a65f.png"
                  alt="DNBio"
                  className="h-5 w-auto opacity-60"
                />
                <span className="text-sm">DNBio Research Grade</span>
              </div>
            </div>

            {/* Product Info */}
            <div>
              {/* Research Categories */}
              <div className="mb-4 flex flex-wrap gap-2">
                {product.researchCategories?.map((category, index) => (
                  <span
                    key={index}
                    className="inline-block px-3 py-1.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full"
                  >
                    {category}
                  </span>
                ))}
              </div>

              <h1 className="text-4xl font-bold text-navy-900 mb-4">
                {product.name}
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                {product.specification}
              </p>

              {/* Research Summary */}
              {product.mechanism && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Mechanism:</strong> {product.mechanism}
                  </p>
                  {product.researchContext && (
                    <p className="text-sm text-gray-700">
                      <strong>Research Context:</strong> {product.researchContext}
                    </p>
                  )}
                </div>
              )}

              {/* Stock Status */}
              <div className="mb-6">
                <span className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 text-sm font-semibold rounded-lg">
                  <i className="ri-checkbox-circle-line mr-2"></i>
                  In Stock
                </span>
              </div>

              {/* Specifications */}
              <div className="bg-white rounded-xl p-6 shadow-md mb-6">
                <h3 className="text-lg font-bold text-navy-900 mb-4">Specifications</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Purity</span>
                    <span className="font-semibold text-navy-900">{product.purity}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Specification</span>
                    <span className="font-semibold text-navy-900">{product.specification}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Testing</span>
                    <span className="font-semibold text-navy-900">HPLC Verified</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">COA Available</span>
                    <span className="font-semibold text-primary-600">Yes</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">MOQ</span>
                    <span className="font-semibold text-navy-900">1 box</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Lead Time</span>
                    <span className="font-semibold text-navy-900">5-10 days</span>
                  </div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-navy-900 mb-2">
                  Quantity (for quote)
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 text-center py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleRequestQuote}
                  className="w-full px-8 py-4 bg-primary-500 text-white text-lg font-semibold rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Request Quote
                </button>
                <a
                  href="https://wa.me/639178850200"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-8 py-4 bg-green-500 text-white text-lg font-semibold rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line mr-2 text-xl"></i>
                  Inquire via WhatsApp
                </a>
              </div>

              {/* Quote Process Notice */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 flex items-start">
                  <i className="ri-information-line text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>All orders require manual quote approval. Payment is processed only after you receive and confirm your quote. No direct checkout available for research compounds.</span>
                </p>
              </div>

              {/* Feature Badges */}
              <div className="mt-8 grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <i className="ri-shield-check-line text-xl text-primary-600"></i>
                  <span>99% Purity</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <i className="ri-test-tube-line text-xl text-primary-600"></i>
                  <span>Third-Party Tested</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <i className="ri-file-list-3-line text-xl text-primary-600"></i>
                  <span>COA Documentation</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <i className="ri-flask-line text-xl text-primary-600"></i>
                  <span>Laboratory Grade</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-12">
            {/* Tab Headers */}
            <div className="border-b border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('description')}
                  className={`px-8 py-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'description'
                      ? 'text-primary-600 border-b-2 border-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Description
                </button>
                <button
                  onClick={() => setActiveTab('additional')}
                  className={`px-8 py-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'additional'
                      ? 'text-primary-600 border-b-2 border-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Additional Information
                </button>
                <button
                  onClick={() => setActiveTab('coa')}
                  className={`px-8 py-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'coa'
                      ? 'text-primary-600 border-b-2 border-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  COA
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {activeTab === 'description' && (
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    This DNBio research-grade {product.name} is manufactured to the highest pharmaceutical standards and undergoes rigorous third-party testing to ensure exceptional purity and quality. Each batch is verified through High-Performance Liquid Chromatography (HPLC) analysis.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Our {product.name} is supplied in sterile, pharmaceutical-grade vials with proper sealing and packaging to maintain compound integrity. All products are stored and shipped under controlled temperature conditions to preserve stability.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    <strong>Storage:</strong> Store at -20°C for long-term storage. Once reconstituted, store at 2-8°C and use within the recommended timeframe. Avoid repeated freeze-thaw cycles.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Quality Assurance:</strong> Certificate of Analysis (COA) is provided with each order, detailing purity results, molecular weight confirmation, and testing methodology.
                  </p>
                </div>
              )}

              {activeTab === 'additional' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Product Name</p>
                      <p className="text-gray-900">{product.name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Specification</p>
                      <p className="text-gray-900">{product.specification}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Purity</p>
                      <p className="text-gray-900">{product.purity}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Testing Method</p>
                      <p className="text-gray-900">HPLC</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Packaging</p>
                      <p className="text-gray-900">Bottled / Boxed</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Shipping</p>
                      <p className="text-gray-900">Door-to-door</p>
                    </div>
                  </div>
                  <div className="mt-6">
                    <p className="text-sm font-semibold text-gray-600 mb-2">Research Categories</p>
                    <div className="flex flex-wrap gap-2">
                      {product.researchCategories?.map((category, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'coa' && (
                <div>
                  <div className="flex items-start space-x-6 mb-6">
                    <div className="w-16 h-16 flex items-center justify-center bg-primary-500 rounded-xl flex-shrink-0">
                      <i className="ri-file-list-3-line text-3xl text-white"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-navy-900 mb-3">
                        Certificate of Analysis (COA)
                      </h3>
                      <p className="text-gray-700 mb-4 leading-relaxed">
                        Every batch of DNBio {product.name} comes with a comprehensive Certificate of Analysis from an independent third-party laboratory. The COA includes HPLC purity results, mass spectrometry data, and complete testing methodology.
                      </p>
                      <Link
                        to="/quality"
                        className="inline-flex items-center px-6 py-3 bg-navy-900 text-white font-semibold rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Learn About Our Testing
                        <i className="ri-arrow-right-line ml-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Compliance Disclaimer Section */}
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6 mb-12">
            <div className="flex items-start">
              <i className="ri-alert-line text-red-600 text-2xl mr-4 mt-1 flex-shrink-0"></i>
              <div>
                <h3 className="text-lg font-bold text-red-900 mb-3">Important Compliance Notice</h3>
                <p className="text-sm text-red-800 leading-relaxed mb-3">
                  <strong>For Research Use Only.</strong> This product is intended solely for laboratory research purposes by qualified researchers and institutions. It is not for human consumption, medical use, veterinary use, or household application.
                </p>
                <p className="text-sm text-red-800 leading-relaxed mb-3">
                  This product is not intended to diagnose, treat, cure, or prevent any disease or medical condition. No medical claims are made or implied. All product information is provided for educational and research purposes only.
                </p>
                <p className="text-sm text-red-800 leading-relaxed">
                  Researchers must handle this product with appropriate safety protocols and comply with all applicable regulations. By requesting a quote, you confirm that you are a qualified researcher purchasing for legitimate laboratory research purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Request Form Modal */}
      {showQuoteForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                  <img
                    src="https://public.readdy.ai/ai/img_res/41008464-2da7-44e8-a4ad-cffce2d1a65f.png"
                    alt="DNBio"
                    className="h-6 w-auto"
                  />
                  <h2 className="text-2xl font-bold text-navy-900">Request Quote</h2>
                </div>
                <button
                  onClick={() => setShowQuoteForm(false)}
                  className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 flex items-center">
                    <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                    Quote request submitted successfully! You can track it in your account dashboard.
                  </p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 flex items-center">
                    <i className="ri-error-warning-line text-xl mr-2"></i>
                    Failed to submit request. Please try again.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmitQuote} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-2">
                    Product Name
                  </label>
                  <input
                    type="text"
                    value={product.name}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-2">
                    Dosage / Pack Size
                  </label>
                  <input
                    type="text"
                    value={product.specification}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-2">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-2">
                    Notes / Special Requests
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    placeholder="Any specific requirements or questions..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-8 py-4 bg-primary-500 text-white text-lg font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'submitting' ? 'Submitting...' : 'Submit Quote Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
